import React, { useLayoutEffect, useState } from 'react'
import { DndProvider } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'

import { useTheme } from '@consta/uikit/Theme'
import classnames from 'classnames'
import { endOfDay, startOfDay } from 'date-fns'

import { Calendar } from './components/Calendar'
import { MonthsSliderRange } from './components/MonthsSliderRange'
import { MonthsSliderSingle } from './components/MonthsSliderSingle'
import { getCurrentVisibleDate, isDateFullyEntered, isDateRange } from './helpers'
import css from './index.css'

export type DateRange = readonly [Date?, Date?]

export const sizes = ['s', 'm', 'l'] as const
export type Size = typeof sizes[number]

export type StyleProps = {
  size?: Size
}

export type DateLimitProps = {
  minDate: Date
  maxDate: Date
}

export type ValueProps<V> = {
  value?: V
}

type RenderControls<V> = (
  props: {
    onChange: (value?: V) => void
    isInvalid: boolean
    tooltipContent?: React.ReactNode
    isCalendarOpened: boolean
  } & ValueProps<V> &
    StyleProps
) => React.ReactElement

type SingleProps = {
  type: 'date'
  onChange: (value?: Date) => void
  renderControls?: RenderControls<Date>
} & ValueProps<Date>

type RangeProps = {
  type: 'date-range'
  onChange: (value?: DateRange) => void
  renderControls?: RenderControls<DateRange>
} & ValueProps<DateRange>

export type Data = DateLimitProps & (SingleProps | RangeProps)

type Props = DateLimitProps & StyleProps & (SingleProps | RangeProps)

export const DatePicker: React.FC<Props> = props => {
  /**
   * Не деструктурируем value и type из объекта props, т.к. при их деструктуризации
   * TypeScript выводит общий тип из объединения в пересечение, пример:
   * ```
   * // Исходные типы:
   * value: Date | DateRange
   * type: 'date' | 'date-range'
   * // Типы после деструктуризации:
   * value: Date & DateRange
   * type: unknown // из-за того, что строка не может быть `date` и `date-range` одновременно
   * ```
   * Вместо деструктуризации в местах обработки value используем type guard isDateRange,
   * чтобы разделять обработку для Date и DateRange через условия, а type можно проверять напрямую
   * без type guard, т.к. без деструктуризации он сохраняет исходный тип date | date-range.
   */
  const { minDate: sourceMinDate, maxDate: sourceMaxDate } = props
  const minDate = startOfDay(sourceMinDate)
  const maxDate = endOfDay(sourceMaxDate)

  const [currentVisibleDate, setCurrentVisibleDate] = useState<Date>(
    getCurrentVisibleDate({ value: props.value, minDate, maxDate })
  )
  const { themeClassNames } = useTheme()

  const baseCommonProps = {
    currentVisibleDate,
    minDate,
    maxDate,
  }
  const monthsPanelCommonProps = {
    ...baseCommonProps,
    onChange: setCurrentVisibleDate,
  }

  const handleSelectDate = (value: Date | DateRange) => {
    if (!isDateRange(value) && props.type === 'date') {
      return props.onChange(value)
    }

    if (isDateRange(value) && props.type === 'date-range') {
      return props.onChange(value)
    }
  }

  useLayoutEffect(() => {
    if (!props.value || !isDateFullyEntered(props.value)) {
      return
    }

    const newVisibleDate = getCurrentVisibleDate({ value: props.value, minDate, maxDate })

    if (currentVisibleDate !== newVisibleDate) {
      setCurrentVisibleDate(newVisibleDate)
    }
    // отключаем проверку, чтобы избежать неявных эффектов, вызванных изменением всех props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.type, props.minDate, props.maxDate])

  return (
    <div className={classnames(themeClassNames.color.invert, css.tooltip)}>
      {props.type === 'date' ? (
        <MonthsSliderSingle {...monthsPanelCommonProps} />
      ) : (
        <DndProvider backend={HTML5Backend}>
          <MonthsSliderRange
            {...monthsPanelCommonProps}
            value={isDateRange(props.value) ? props.value : undefined}
          />
        </DndProvider>
      )}
      <Calendar {...baseCommonProps} value={props.value} onSelect={handleSelectDate} />
    </div>
  )
}
